import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, from, lastValueFrom } from 'rxjs';
import { tap, take, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ILogAudit } from '../core/models/log-audit.model';
import { ItineraryService } from '../search-result/services/itinerary.service';
import { AuthFactoryService } from './authFactory.service';
import { APIService } from '../core/api/general.service';
import { ActionType, ActionTypeText, StatusType } from '../core/enums/enums';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';

interface IBaseLogAuditData {
  type: string;
  subtype: string;
  pnr: string;
  gds: string;
  bookingCode: string;
}

const unableToSaveReportLog = 'Unable to save report log';
const logsEnabled = false;

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  private env = environment;
  private token: string = 'N/A';
  baseLogAuditData: IBaseLogAuditData = {
    type: '',
    subtype: '',
    pnr: '',
    gds: '',
    bookingCode: ''
  };

  logHttpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(
    private authFactoryService: AuthFactoryService,
    private itineraryService: ItineraryService,
    private router: Router,
    private http: HttpClient,
    private api: APIService
  ) {}

  urlEnabled(request: HttpRequest<any>) {
    return (
      request.url.includes(`${this.env.involuntaryBook}`) ||
      request.url.includes(`${this.env.voluntaryChangeBook}`) ||
      request.url.includes(`${this.env.involuntaryNoShowRegulation}`)
    );
  }

  rules(request: HttpRequest<any>) {
    return (
      request.url !== `${this.env.urlEnvPostRedirect}/assets/i18n/en.json` &&
      request.url !== `${this.env.urlEnvPostRedirect}/assets/i18n/es-CO.json` &&
      request.url !== `${this.env.urlEnvPostRedirect}/assets/i18n/es-MX.json` &&
      request.url !== `${this.env.urlEnvPostRedirect}/assets/i18n/pt.json` &&
      request.url !== `${this.env.urlAuthority}/connect/token` &&
      request.url !== `${this.env.urlAuthority}/.well-known/openid-configuration` &&
      request.url !== `${this.env.urlAuthority}/.well-known/openid-configuration/jwks` &&
      request.url !== `${this.env.urlAuthorityCt}/connect/token` &&
      request.url !== `${this.env.urlAuthorityCt}/.well-known/openid-configuration` &&
      request.url !== `${this.env.urlAuthorityCt}/.well-known/openid-configuration/jwks` &&
      request.url !== `${this.env.urlAuthorityAM}` &&
      request.url !== `${this.env.urlAuthorityAM}/authorize` &&
      request.url !== `${this.env.urlAuthorityAM}/oauth/token` &&
      request.url !== `${this.env.urlAdminBase}${this.env.users}` &&
      request.url !== `${this.env.urlLogApi}${this.env.logsAudit}`
    );
  }

  errorRules(errorResponse: HttpErrorResponse): boolean {
    return (
      !window.location.host.includes('8089') &&
      errorResponse.url != `${this.env.urlLogApi}${this.env.logsAudit}` &&
      (errorResponse?.status === 400 ||
        errorResponse?.status === 404 ||
        errorResponse?.status === 409 ||
        errorResponse?.status === 500 ||
        errorResponse?.status === 502 ||
        errorResponse?.status === 503)
    );
  }

  /**
   * Log service
   */

  startTimeMeasure() {
    const startTime = new Date().getTime() / 1000;
    return startTime;
  }

  endTimeMeasure(startTime: number, endTime: number): string {
    const timeDiff = endTime - startTime;
    const seconds = Math.round(timeDiff).toString();
    return seconds;
  }

  getDateMeasure(): string {
    const today = new Date();
    return today.toISOString();
  }

  createLogAudit(
    tokenId: string,
    req: HttpRequest<any> | string,
    res: HttpResponse<any> | HttpErrorResponse | string,
    startTime: string,
    reqDuration: string,
    type: string,
    subtype: string,
    pnr?: string,
    gds?: string,
    bookingCode?: string,
    transactionId?: string
  ): ILogAudit {
    return {
      TokenId: tokenId,
      Request: req,
      Response: res,
      ManagementStartTime: startTime,
      ManagementDuration: reqDuration,
      ApiId: 'OperationWeb',
      PaxResponse: true,
      ManagementType: type ? type : 'N/A',
      ManagementSubType: subtype ? subtype : 'N/A',
      Pnr: pnr ? pnr : 'N/A',
      Gds: gds ? gds : 'N/A',
      BookCode: bookingCode ? bookingCode : 'N/A',
      TransactionId: transactionId ? transactionId : 'No TransactionID'
    };
  }

  postLogAudit(logAudit: ILogAudit) {
    this.http
      .post(`${this.env.urlLogApi}${this.env.logsAudit}`, logAudit, this.logHttpOptions)
      .subscribe((res) => {
        /**
         * !DO NOT REMOVE THIS PROMISE SUBSCRIPTION
         * !IT IS NECESARY TO THE SERVICE TO WORK
         */
      });
  }

  /**
   * End log service
   */

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.handleToken(request, next));
  }

  async handleToken(request: HttpRequest<any>, next: HttpHandler) {
    let startTime = new Date().getTime() / 1000;

    if (this.rules(request)) {
      const authToken = await lastValueFrom(this.authFactoryService.getAccessToken());

      if (authToken) {
        this.token = authToken;
      }

      const logRequested = request.url.split('/').indexOf('logData');

      if (logRequested > 0) {
        let counter = 0;
        request.url.split('/').forEach((element, index) => {
          if (index >= logRequested) {
            switch (counter) {
              case 1:
                this.baseLogAuditData.type = element;
                break;

              case 2:
                this.baseLogAuditData.subtype = element;
                break;

              case 3:
                this.baseLogAuditData.pnr = element;
                break;

              case 4:
                this.baseLogAuditData.gds = element;
                break;

              case 5:
                this.baseLogAuditData.bookingCode = element;
                break;

              default:
                break;
            }
            counter++;
          }
        });

        const rebuildOriginalRequestUrl = request.url.split('/logData/')[0];
        request = request.clone({ url: rebuildOriginalRequestUrl });
        /**
         * !Do not delete comment below
         * requestHasLog = true
         */
      }

      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${authToken}`
        }
      });
      request = request.clone({
        headers: request.headers.append('Access-Control-Allow-Origin', '*')
      });
      request = request.clone({
        headers: request.headers.append('Access-Control-Allow-Headers', 'Content-Type')
      });
      request = request.clone({
        headers: request.headers.append(
          'Access-Control-Allow-Methods',
          'GET,POST,OPTIONS,DELETE,PUT'
        )
      });
    }

    return await lastValueFrom(
      next.handle(request).pipe(
        tap({
          next: (event) => {
            if (event instanceof HttpResponse) {
              /**
               * !Log request temporally disabled (requestHasLog --> logsEnabled)
               */
              if ((logsEnabled && this.rules(request)) || this.urlEnabled(request)) {
                let endTime = new Date().getTime() / 1000;
                this.postLogAudit(
                  this.createLogAudit(
                    this.token,
                    request,
                    event,
                    this.getDateMeasure(),
                    this.endTimeMeasure(startTime, endTime),
                    this.baseLogAuditData.type,
                    this.baseLogAuditData.subtype,
                    this.baseLogAuditData.pnr,
                    this.baseLogAuditData.gds,
                    this.baseLogAuditData.bookingCode,
                    event.body?.transactionId ? event.body?.transactionId : ''
                  )
                );
              }
            }

            if (
              event instanceof HttpResponse &&
              (request.url.includes('PnrTypeSearch') || request.url.includes('search/tickets'))
            ) {
              setTimeout(() => {
                this.Log(ActionTypeText.NA, StatusType.Search, '', startTime); //TODO: refactor using report-log.service
              }, 200);
            }
          },
          error: (errorResponse): Observable<never> => {
            this.manageErrors(errorResponse, request, startTime);
            return errorResponse;
          }
        })
      )
    );
  }

  private manageErrors(
    response: HttpErrorResponse,
    request: HttpRequest<any>,
    startTime: number
  ): void {
    if (response?.status === 401) {
      this.router.parseUrl('/unauthorized');
    }

    if (this.errorRules(response)) {
      this.itineraryService
        .getPnrInfo()
        .subscribe((pnr) => {
          if (!!pnr?.transactionId) {
            const logAudit = this.createLogAudit(
              this.token,
              JSON.stringify(request),
              JSON.stringify(response),
              this.getDateMeasure(),
              this.endTimeMeasure(startTime, new Date().getTime() / 1000),
              'errorFront',
              'errorFront',
              this.baseLogAuditData.pnr,
              this.baseLogAuditData.gds,
              this.baseLogAuditData.bookingCode,
              pnr?.transactionId
            );
            this.postLogAudit(logAudit);
          } else {
            const logAudit = this.createLogAudit(
              this.token,
              JSON.stringify(request),
              JSON.stringify(response),
              this.getDateMeasure(),
              this.endTimeMeasure(startTime, new Date().getTime() / 1000),
              'errorFront',
              'errorFront',
              this.baseLogAuditData.pnr,
              this.baseLogAuditData.gds,
              this.baseLogAuditData.bookingCode,
              'No TransactionID'
            );
            this.postLogAudit(logAudit);
          }
        })
        .unsubscribe();
    }
  }

  private Log(
    action: keyof typeof ActionType,
    status: StatusType,
    error: string = '',
    startTime: number
  ) {
    let TransactionID: string;

    this.itineraryService
      .getPnrInfo()
      .pipe(take(1))
      .subscribe({
        next: (form) => {
          TransactionID = form?.transactionId;
        }
      });

    this.itineraryService
      .getSearchForm()
      .pipe(take(1))
      .subscribe({
        next: (form) => {
          const params: any = {
            action: ActionType[action],
            status,
            gds: form.gds,
            pnr: form.ticketNumbers?.length ? form.ticketNumbers.toString() : form.pnr,
            time: this.endTimeMeasure(startTime, new Date().getTime() / 1000),
            transactionId: TransactionID
          };
          if (error) {
            params.error = error;
          }
          this.api
            .saveReportLog(params)
            .pipe(take(1))
            .subscribe({
              error: (ex) => {
                console.warn(unableToSaveReportLog, {
                  body: params,
                  internalError: ex
                });
              }
            });
        },
        error: console.error
      });
  }
}
